import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import type {
  ConversationIndex,
  ConversationIndexListFetchResult,
  SearchConversationIndex,
} from '@/core/conversations/conversation-index/conversation-index.type';
import { JobApplicantStatus } from '@factoryfixinc/ats-interfaces';
import { SearchConversationOrderByFields } from '@/core/conversations/conversation-index/conversation-index.type';

export const useConversationIndexStore = defineStore(
  'conversationIndex',
  () => {
    const selectedConversationIndex = ref<ConversationIndex | null>();
    const conversationIndexes = ref<ConversationIndex[] | []>();
    const loadingConversationIndexes = ref<boolean>(true);
    const loadingMoreConversations = ref<boolean>(false);
    const selectingConversationIndex = ref<boolean>(false);
    const selectedStatusList = ref<string[]>([]);
    const showSearchField = ref<boolean>(false);
    const visitedTabRouteName = ref<string>('');
    const selectedStatusFilters = ref<string[]>([]);
    const lastPageWithResults = ref<number | null>(null);
    // Monotonic id so only the newest list load publishes its outcome.
    const listFetchRequestId = ref<number>(0);
    const latestListFetchResult = ref<ConversationIndexListFetchResult | null>(null);
    const scrollMessagesToBottom = ref<boolean>(false);

    const conversationSearch = ref<SearchConversationIndex>({
      onlyCurrentUser: false,
      onlyUnread: false,
      jobIds: [],
      conversationIds: [],
      applicationStatus: [
        JobApplicantStatus.CONTACT,
        JobApplicantStatus.NEW,
        JobApplicantStatus.CLIENT,
        JobApplicantStatus.REVIEW,
        JobApplicantStatus.INTERVIEW,
        JobApplicantStatus.OFFER,
        JobApplicantStatus.HIRED,
      ],
      origins: [],
      orderBy: {
        field: SearchConversationOrderByFields.LAST_MESSAGE_SENT_TS,
        direction: 'desc',
      },
      pagination: {
        page: 1,
        itemsPerPage: 25 as 25 | 50 | 75 | 100,
      },
    });

    // Candidate archive page
    const lastCandidateArchiveSearch = ref<string>('');

    // Account-wide candidate search (ONE-6912). Kept fully separate from
    // `conversationSearch`/`conversationIndexes` so an active search never
    // overwrites the resting job/stage conversation list.
    const globalSearchQuery = ref<string>('');
    const globalSearchResults = ref<ConversationIndex[]>([]);
    // True when the backend matched more candidates than the 100 it returned.
    // The strict endpoint reports no exact total (ONE-6912 follow-up: the
    // per-keystroke COUNT(*) was dropped), only this over-fetch indicator.
    const globalSearchHasMore = ref<boolean>(false);
    const globalSearchLoading = ref<boolean>(false);
    const globalSearchError = ref<boolean>(false);
    // The last query a response (success or error) has settled for. Empty-state
    // rendering keys off this so an empty state never flashes early.
    const globalSearchSettledQuery = ref<string>('');
    // Monotonic id used to drop stale responses when queries race.
    const globalSearchRequestId = ref<number>(0);

    // Monotonic ids used to drop stale conversation-list responses and stale
    // candidate selections when job/stage switches or clicks overlap.
    const conversationListRequestId = ref<number>(0);
    const selectionRequestId = ref<number>(0);
    // The list generation of the job switch in progress, until its route run ends.
    const jobSwitchRequestId = ref<number | null>(null);
    // Shared across card instances so a later card click cancels an earlier one's job lookup.
    const cardSelectionController = shallowRef<AbortController | null>(null);

    // alphabetized list
    return {
      cardSelectionController,
      conversationIndexes,
      conversationListRequestId,
      conversationSearch,
      globalSearchError,
      globalSearchHasMore,
      globalSearchLoading,
      globalSearchQuery,
      globalSearchRequestId,
      globalSearchResults,
      globalSearchSettledQuery,
      jobSwitchRequestId,
      lastCandidateArchiveSearch,
      lastPageWithResults,
      latestListFetchResult,
      listFetchRequestId,
      loadingConversationIndexes,
      loadingMoreConversations,
      scrollMessagesToBottom,
      selectedConversationIndex,
      selectedStatusFilters,
      selectedStatusList,
      selectingConversationIndex,
      selectionRequestId,
      showSearchField,
      visitedTabRouteName,
    };
  },
  {
    persist: false,
  },
);
